import type { KeyboardEvent } from 'react'
import { dragWidth } from '@/utils/drag-util'
import styles from './ResizeHandle.module.css'

export interface ResizeHandleProps {
  width: number
  minWidth: number
  maxWidth: number
  /**
   * Called as the width changes, with a width that may be out of range.
   */
  onResize: (width: number) => void
  /**
   * Called once a drag or key press ends.
   */
  onResizeEnd: () => void
  onReset: () => void
  'aria-label': string
}

const keyboardStep = 16

/**
 * Drag handle on the right edge of a `position: relative` panel. Arrow keys
 * resize it too, and a double click resets it.
 */
export function ResizeHandle({
  width,
  minWidth,
  maxWidth,
  onResize,
  onResizeEnd,
  onReset,
  'aria-label': ariaLabel,
}: ResizeHandleProps) {
  const resizeWithKeyboard = (e: KeyboardEvent<HTMLDivElement>) => {
    const direction =
      e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!direction) {
      return
    }
    e.preventDefault()
    onResize(width + direction * keyboardStep * (e.shiftKey ? 4 : 1))
    onResizeEnd()
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- a focusable separator is the ARIA window splitter widget, which jsx-a11y doesn't know is interactive
    <div
      className={styles.root}
      role="separator"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- see above
      tabIndex={0}
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={width}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      onPointerDown={(e) => dragWidth(e, width, onResize, onResizeEnd)}
      onKeyDown={resizeWithKeyboard}
      onDoubleClick={onReset}
    />
  )
}
